/**
 * Cash-first paid-intent claim (SYN-1449).
 *
 * After the accept-invitation flow activates the buyer's organization, this
 * calls Command's `POST /api/onboarding/org`, which forwards the buyer's
 * bearer to Atlas's idempotent claim+provision endpoint (subscription,
 * included credits, ACSS §13 identity bootstrap, first-party catalog).
 *
 * Atlas answers 409 `paid_intent_pending` while the Stripe webhook is still
 * settling the payment — that is retried with backoff. Any other failure is
 * surfaced to the page so the buyer is NOT silently navigated into an
 * unprovisioned workspace; resubmitting is safe because the upstream is
 * idempotent.
 */

import { fetchWithAuth } from "./api";

export interface ClaimPaidIntentInput {
  orgId: string;
  intentId?: string | null;
  checkoutSessionId?: string | null;
}

export type ClaimPaidIntentResult =
  | { ok: true; redirectPath: string | null }
  | { ok: false; error: string };

interface ClaimOptions {
  /** Injection points for tests. */
  fetcher?: typeof fetchWithAuth;
  sleep?: (ms: number) => Promise<void>;
  maxAttempts?: number;
}

/** Backoff between paid_intent_pending retries; total wait ≈ 18s, well inside
 * the 3-minute cash-to-Command budget. */
const RETRY_DELAYS_MS = [2_000, 3_000, 5_000, 8_000];
const DEFAULT_MAX_ATTEMPTS = RETRY_DELAYS_MS.length + 1;

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function readPaidIntentMetadata(metadata: unknown): {
  intentId: string | null;
  checkoutSessionId: string | null;
} {
  if (!metadata || typeof metadata !== "object") {
    return { intentId: null, checkoutSessionId: null };
  }
  const record = metadata as Record<string, unknown>;
  const intentId = typeof record.intent_id === "string" ? record.intent_id : null;
  const checkoutSessionId =
    typeof record.checkout_session_id === "string" ? record.checkout_session_id : null;
  return { intentId, checkoutSessionId };
}

/**
 * Read the invitation's paid-intent context from the buyer's Clerk
 * `publicMetadata` (stamped by the Atlas Stripe webhook on the invitation).
 * Atlas also resolves these server-side, so missing metadata is not fatal —
 * the claim is still made with just the orgId.
 */
export function paidIntentContextFromClerkUser(user: unknown): {
  intentId: string | null;
  checkoutSessionId: string | null;
} {
  if (!user || typeof user !== "object") {
    return { intentId: null, checkoutSessionId: null };
  }
  return readPaidIntentMetadata((user as { publicMetadata?: unknown }).publicMetadata);
}

interface ClaimResponseBody {
  error?: string;
  code?: string;
  retryable?: boolean;
  redirectPath?: string;
}

async function parseBody(response: Response): Promise<ClaimResponseBody> {
  try {
    const parsed: unknown = await response.json();
    return parsed && typeof parsed === "object" ? (parsed as ClaimResponseBody) : {};
  } catch {
    return {};
  }
}

export async function claimPaidIntent(
  input: ClaimPaidIntentInput,
  options: ClaimOptions = {},
): Promise<ClaimPaidIntentResult> {
  const fetcher = options.fetcher ?? fetchWithAuth;
  const sleep = options.sleep ?? defaultSleep;
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;

  let lastError = "Could not activate the paid subscription.";
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await sleep(RETRY_DELAYS_MS[Math.min(attempt - 1, RETRY_DELAYS_MS.length - 1)]);
    }

    let response: Response;
    try {
      response = await fetcher("/api/onboarding/org", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          // The buyer's JWT may not yet carry the just-activated org claim;
          // the gateway accepts X-Org-Context as the org-context fallback and
          // Atlas verifies real membership server-side.
          "X-Org-Context": input.orgId,
        },
        body: JSON.stringify({
          orgId: input.orgId,
          ...(input.intentId ? { intentId: input.intentId } : {}),
          ...(input.checkoutSessionId ? { checkoutSessionId: input.checkoutSessionId } : {}),
        }),
      });
    } catch {
      lastError = "Could not reach Command to activate the subscription. Please try again.";
      continue;
    }

    if (response.ok) {
      const body = await parseBody(response);
      return {
        ok: true,
        redirectPath: typeof body.redirectPath === "string" ? body.redirectPath : null,
      };
    }

    const body = await parseBody(response);
    // 409 paid_intent_pending: the Stripe webhook is still settling. 5xx: a
    // transient failure on Command's or Atlas's side (e.g. the local-sync or
    // identity-spine steps in handleOnboardingClaimRequest, which mark their
    // 502s `retryable: true` or `retryable: false`) — retry by default since
    // the claim is idempotent and most 5xx here are transient, but honor an
    // explicit `retryable: false` (e.g. a non-retryable identity-spine
    // blocker) instead of retrying forever (SYN-2096).
    const retryableUpstream =
      (response.status === 409 &&
        (body.code === "paid_intent_pending" || body.retryable === true)) ||
      (response.status >= 500 && response.status < 600 && body.retryable !== false);
    lastError =
      typeof body.error === "string" && body.error
        ? body.error
        : `Subscription activation failed (${response.status}).`;
    if (!retryableUpstream) {
      return { ok: false, error: lastError };
    }
  }

  return { ok: false, error: lastError };
}
